/* eslint-disable no-unused-vars */
/* global G, PLATFORM */
import { asyncPipeSpread, asyncPipeSpreadIf } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import sequenceComponentState from 'lib/sequence/component/state'
import routeComposition from 'trait/composition/route'
import redirectSequence from 'lib/sequence/module/adapter/router/redirect'
import settings from '@tenant/settings'

const {
  set: setUrl,
} = sequenceComponentState('url')

/**
 * Attempts to redirect to the `external` action of the `guest` module.
 *
 * @param {Gaia.AppModule.Spec} module                  the current module object composition
 * @param {{ url:string, params:Object }} externalArgs  arguments used by the `external` action
 * @returns {Promise<void>}
 */
export const redirectToExternal = async (module, externalArgs) => {
  const moduleState = module[G.STATE]
  moduleState[G.ROUTE] = module[G.CONFIGURATION].name === 'guest'
    ? routeComposition(null, module[G.ACTIONS].external._name)
    : routeComposition('guest', 'external')
  await redirectSequence(module)(externalArgs)
}

/**
 * Attempts to open `url` and `params`, passed as the action call arguments, in a new browser tab.
 *
 * @param {Gaia.AppModule.Spec} module - application module
 * @returns {function(*, *, ...[*]): *[]}
 */
const openTab = module => (components, ...args) => {
  const [{ url: baseUrl = settings.externalPortalUrl, params } = {}] = args || [{}]

  module[G.ADAPTER][G.ROUTER].open({ url: baseUrl, params })

  return [components, ...args]
}

/**
 * Takes `url` and `params` from `args` and sets them as the `url` prop of the `WebView` component.
 *
 * @param {Gaia.AppModule.Spec} module - application module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayWebView = module => async (components, ...args) => {
  const [{ url: baseUrl = settings.externalPortalUrl, params } = {}] = args || [{}]
  const { web } = components

  const targetUrl = new URL(baseUrl)
  targetUrl.search = decodeURIComponent(new URLSearchParams(params).toString())

  setUrl(web, targetUrl.href)

  await module[G.ADAPTER][G.UI].update(module)

  return args
}

/**
 * External action
 *
 * Intended to display and handle an external login form.
 *
 * @param {Gaia.AppModule.Spec} module - application module
 * @returns {function(*): function(*): *}
 */
export default module => component => async (...args) => asyncPipeSpread(
  asyncPipeSpreadIf(() => PLATFORM === 'web')(
    openTab(module),
  ),
  asyncPipeSpreadIf(() => PLATFORM === 'mobile')(
    displayWebView(module),
  ),
  (components, ...argv) => argv,
)(find(component), ...args)
